// src/pages/MessagesPage.jsx
import { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate, useOutletContext } from "react-router-dom";

import { motion, AnimatePresence } from "framer-motion";
import Avatar from "../components/Avatar";

function MessagesPage() {
  
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId) return;

    const fetchChats = async () => {
      try {
        const q = query(
          collection(db, "chats"),
          where("users", "array-contains", userId),
          orderBy("timestamp", "desc")
        );
        const snapshot = await getDocs(q);

        const chatList = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const chatData = docSnap.data();
            const otherUserId = chatData.users.find((uid) => uid !== userId);
            const otherUserSnap = await getDoc(doc(db, "users", otherUserId));
            const otherUser = otherUserSnap.exists() ? otherUserSnap.data() : null;

            return {
              id: docSnap.id,
              otherUserId,
              otherUsername: otherUser?.username || "Bilinmiyor",
              otherPhoto: otherUser?.avatar || otherUser?.profilePhoto || null,
              lastMessage: chatData.lastMessage,
              timestamp: chatData.timestamp?.toDate().toLocaleString() || "",
            };
          })
        );

        setChats(chatList);
      } catch (err) {
        setError(`Sohbetler yüklenirken hata oluştu: ${err.message}`);
      }
    };

    fetchChats();
  }, [userId]);

  

  return (
    <motion.div
      style={containerStyle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.h2
        style={headerStyle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        📨 Mesajlar
      </motion.h2>
      {chats.length === 0 ? (
        <motion.p
          style={emptyMessageStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          Henüz bir sohbetiniz yok.
        </motion.p>
      ) : (
        <motion.div
          style={chatListStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <AnimatePresence>
            {chats.map((chat, index) => (
              <motion.div
                key={chat.id}
                onClick={() => navigate(`/chat/${chat.otherUserId}`)}
                style={chatItemStyle}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div style={avatarContainerStyle}>
                  <Avatar avatarData={chat.otherPhoto} seedFallback={chat.otherUsername} size={54} />
                </div>
                <div style={chatContentStyle}>
                  <strong style={chatUsernameStyle}>@{chat.otherUsername}</strong>
                  <p style={chatMessageStyle}>
                    {chat.lastMessage || "Henüz mesaj yok."}
                  </p>
                </div>
                <div style={chatTimestampStyle}>{chat.timestamp}</div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      <AnimatePresence>
        {error && (
          <motion.p
            style={errorTextStyle}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const containerStyle = {
  background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
  minHeight: "100vh",
  padding: "40px",
  fontFamily: "'Inter', sans-serif",
  color: "#f0f0f0",
};

const headerStyle = {
  fontSize: "28px",
  textAlign: "center",
  marginBottom: "20px",
  color: "#00ffcc",
  textShadow: "0 0 10px rgba(0, 230, 118, 0.3)",
};

const emptyMessageStyle = {
  textAlign: "center",
  fontSize: "18px",
  color: "#b0bec5",
};

const chatListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const chatItemStyle = {
  background: "rgba(255, 255, 255, 0.05)",
  padding: "16px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(0, 230, 118, 0.2)",
};

const avatarContainerStyle = {
  width: "60px",
  height: "60px",
  borderRadius: "50%",
  overflow: "hidden",
  marginRight: "20px",
  border: "3px solid #00ffcc",
};

const avatarImageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const avatarPlaceholderStyle = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "32px",
  color: "#b0bec5",
  background: "rgba(255, 255, 255, 0.1)",
};

const chatContentStyle = {
  flex: 1,
};

const chatUsernameStyle = {
  fontSize: "20px",
  color: "#00ffcc",
};

const chatMessageStyle = {
  fontSize: "14px",
  color: "#ccc",
  marginTop: "4px",
};

const chatTimestampStyle = {
  fontSize: "12px",
  color: "#888",
};

const errorTextStyle = {
  marginTop: "20px",
  textAlign: "center",
  fontSize: "14px",
  color: "#f44336",
};

export default MessagesPage;